/**
 * §58 per-hauler feature gating: the feature catalog and the template defaults.
 *
 * The database holds the same catalog (migration 0194: platform_features and
 * platform_feature_template_defaults), and an integration test pins the two equal, so the
 * API, the apps and the database cannot drift. Adding a feature is a migration that seeds
 * it plus an entry here.
 *
 * Effective state, per hauler (tenant_effective_features() in 0194):
 *   a data-capture feature is always on; else Paelode's override, while it has not
 *   expired; else the hauler's template default. A hauler with no plan row is STARTER.
 *
 * Haulers never see a template name (§58: no tier names, no pricing hints). The apps get
 * only `EffectiveFeatureMap`.
 */

export const FEATURE_TEMPLATES = ['EXPRESS', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'] as const;
export type FeatureTemplate = (typeof FEATURE_TEMPLATES)[number];

/** A hauler with no plan row reads as this template (design Q11). */
export const DEFAULT_FEATURE_TEMPLATE: FeatureTemplate = 'STARTER';

export const FEATURE_GROUPS = ['CORE', 'COMPLIANCE', 'INTELLIGENCE', 'ADVANCED', 'IOT'] as const;
export type FeatureGroup = (typeof FEATURE_GROUPS)[number];

/** A template default: on or off, and a limit for a feature that has one. */
export interface FeatureDefault {
  readonly enabled: boolean;
  readonly limit?: number;
}

export interface FeatureDefinition {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly group: FeatureGroup;
  /** Always runs, whatever the template or an override says (§58 data capture). */
  readonly isDataCapture: boolean;
  /**
   * Carries a numeric limit when enabled. No hauler feature has one since OPP PR 13a retired
   * OPERATOR_API (operator API keys are the operator's OP_API feature since PR 9).
   */
  readonly hasLimit: boolean;
  readonly sortOrder: number;
  readonly defaults: Readonly<Record<FeatureTemplate, FeatureDefault>>;
}

const ON: FeatureDefault = { enabled: true };
const OFF: FeatureDefault = { enabled: false };

/** On in every template. */
const ALL = { EXPRESS: ON, STARTER: ON, PROFESSIONAL: ON, ENTERPRISE: ON } as const;
/** PROFESSIONAL and ENTERPRISE. */
const PRO = { EXPRESS: OFF, STARTER: OFF, PROFESSIONAL: ON, ENTERPRISE: ON } as const;
/** ENTERPRISE only. */
const ENT = { EXPRESS: OFF, STARTER: OFF, PROFESSIONAL: OFF, ENTERPRISE: ON } as const;
/** Never on by default: a per-hauler override only. */
const NONE = { EXPRESS: OFF, STARTER: OFF, PROFESSIONAL: OFF, ENTERPRISE: OFF } as const;

/**
 * The catalog, in display order. Corbin's rulings (2026-10-16, design Q1–Q14): EXPRESS has
 * exactly STARTER's features; compliance is on everywhere; the live map is ENTERPRISE
 * only; the Tanks tab is PROFESSIONAL and up; the AI analyst, MQTT and the data archive are
 * never on by default. The full/basic dashboards are not here until a basic version
 * exists (Q5).
 */
export const FEATURE_CATALOG = [
  {
    id: 'DATA_CAPTURE_ACTUAL_MILES',
    name: 'Actual miles driven',
    description: 'Per-load miles from the GPS trail. Always captured.',
    group: 'CORE',
    isDataCapture: true,
    hasLimit: false,
    sortOrder: 10,
    defaults: ALL,
  },
  {
    id: 'DATA_CAPTURE_DISPOSAL_SELECTION',
    name: 'Disposal selection events',
    description:
      'Which disposal was picked and whether a recommendation was shown. Always captured.',
    group: 'CORE',
    isDataCapture: true,
    hasLimit: false,
    sortOrder: 20,
    defaults: ALL,
  },
  {
    id: 'DATA_CAPTURE_GPS_VERIFICATION',
    name: 'GPS verification',
    description: 'Arrival verification against geofences. Always captured.',
    group: 'CORE',
    isDataCapture: true,
    hasLimit: false,
    sortOrder: 30,
    defaults: ALL,
  },
  {
    id: 'DATA_CAPTURE_SYNC_EVENTS',
    name: 'Sync notification events',
    description: 'Sync nudges and driver check-ins. Always captured.',
    group: 'CORE',
    isDataCapture: true,
    hasLimit: false,
    sortOrder: 40,
    defaults: ALL,
  },
  {
    id: 'COMPLIANCE_OK_1002A',
    name: 'Oklahoma OCC Form 1002A',
    description: 'Generate and deliver the Oklahoma report.',
    group: 'COMPLIANCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 100,
    defaults: ALL,
  },
  {
    id: 'COMPLIANCE_TX_H11',
    name: 'Texas RRC Form H-11',
    description: 'Generate and deliver the Texas report.',
    group: 'COMPLIANCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 110,
    defaults: ALL,
  },
  {
    id: 'COMPLIANCE_NM_C115',
    name: 'New Mexico OCD Form C-115',
    description: 'Generate and deliver the New Mexico report.',
    group: 'COMPLIANCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 120,
    defaults: ALL,
  },
  {
    id: 'COMPLIANCE_WY_FORM3',
    name: 'Wyoming WOGCC Form 3',
    description: 'Generate and deliver the Wyoming report.',
    group: 'COMPLIANCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 130,
    defaults: ALL,
  },
  {
    id: 'COMPLIANCE_ND_NDIC',
    name: 'North Dakota NDIC monthly',
    description: 'Generate and deliver the North Dakota report.',
    group: 'COMPLIANCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 140,
    defaults: ALL,
  },
  {
    id: 'SWD_MORNING_SUMMARY',
    name: 'SWD morning summary',
    description: "The hauler's morning disposal summary.",
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 200,
    defaults: PRO,
  },
  {
    id: 'DISPOSAL_RECOMMENDATIONS',
    name: 'Disposal recommendations',
    description: 'Ranked disposal choices for dispatchers and drivers.',
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 210,
    defaults: PRO,
  },
  {
    id: 'SITE_DISPOSAL_MILEAGE',
    name: 'Site-to-disposal mileage',
    description: 'Measured miles between each site and disposal.',
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 220,
    defaults: PRO,
  },
  {
    id: 'BID_CALCULATOR',
    name: 'Bid calculator',
    description: 'Price a bid from measured costs.',
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 230,
    defaults: PRO,
  },
  {
    id: 'DISPOSAL_PREFERENCES',
    name: 'Disposal preferences',
    description: 'Customer and site disposal preferences and exclusions.',
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 240,
    defaults: PRO,
  },
  {
    id: 'FLEET_MILEAGE_DASHBOARD',
    name: 'Fleet mileage dashboard',
    description: 'Miles by truck, driver and customer.',
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 250,
    defaults: PRO,
  },
  {
    id: 'DRIVER_GPS_ANALYTICS',
    name: 'Driver GPS analytics',
    description: 'GPS verification rates and trends per driver.',
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 260,
    defaults: PRO,
  },
  {
    id: 'SYNC_ANALYTICS',
    name: 'Sync analytics',
    description: 'Sync delay and nudge metrics.',
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 270,
    defaults: PRO,
  },
  {
    id: 'DISPOSAL_SELECTION_ANALYTICS',
    name: 'Disposal selection analytics',
    description: 'How disposal choices compare with the recommendation.',
    group: 'INTELLIGENCE',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 280,
    defaults: ENT,
  },
  {
    id: 'LIVE_MAP',
    name: 'Live map',
    description: 'The Map View with live truck positions.',
    group: 'ADVANCED',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 400,
    defaults: ENT,
  },
  {
    id: 'TANKS_TAB',
    name: 'Tanks tab',
    description: 'The dispatcher Tanks tab.',
    group: 'ADVANCED',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 410,
    defaults: PRO,
  },
  {
    // Phase 13.4a (0236; Corbin 2026-10-19): a pending haul is created when a tank is predicted
    // to reach its threshold. Also switched on per site (off by default); 13.4b's sweeper acts only
    // where both are on.
    id: 'AUTO_HAUL',
    name: 'Auto-haul',
    description: 'A pending haul is created when a tank is predicted to reach its threshold.',
    group: 'ADVANCED',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 415,
    defaults: PRO,
  },
  {
    id: 'ESG_REPORTING',
    name: 'ESG reporting',
    description: 'Emissions and environmental reporting.',
    group: 'ADVANCED',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 420,
    defaults: ENT,
  },
  {
    id: 'WHITE_LABEL',
    name: 'White-label',
    description: 'Hauler branding beyond the company logo.',
    group: 'ADVANCED',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 430,
    defaults: ENT,
  },
  {
    id: 'AI_OPS_ANALYST',
    name: 'AI Operations Analyst',
    description: 'The AI operations assistant. Enable per hauler only.',
    group: 'ADVANCED',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 440,
    defaults: NONE,
  },
  {
    id: 'DATA_ARCHIVE',
    name: 'Data archive',
    description: 'History beyond the standard retention. Add-on.',
    group: 'ADVANCED',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 450,
    defaults: NONE,
  },
  {
    id: 'MQTT_TANK_GAUGE_INTEGRATION',
    name: 'MQTT tank gauge integration',
    description: 'Live tank gauges over MQTT. Add-on, after a commercial agreement.',
    group: 'IOT',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 500,
    defaults: NONE,
  },
] as const satisfies readonly FeatureDefinition[];

export type HaulerFeatureId = (typeof FEATURE_CATALOG)[number]['id'];

// ── Operator portal PR 6 (migration 0212): OPERATOR tenants' features ─────────────
//
// The same model as a hauler's, with its own templates. platform_features.applies_to keeps
// the two apart: tenant_effective_features() gives a hauler only hauler features and an
// operator only these. Operators never see a template name either.

export const OPERATOR_TEMPLATES = ['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE'] as const;
export type OperatorTemplate = (typeof OPERATOR_TEMPLATES)[number];

/** An operator with no plan row reads as this template. */
export const DEFAULT_OPERATOR_TEMPLATE: OperatorTemplate = 'FREE';

export interface OperatorFeatureDefinition {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly group: 'OPERATOR_PORTAL';
  readonly isDataCapture: false;
  /**
   * Carries a numeric limit when enabled. Unlike a hauler feature, an operator feature may be
   * on with NO limit, meaning unlimited (OP_TICKETS on PREMIUM: all history).
   */
  readonly hasLimit: boolean;
  readonly sortOrder: number;
  readonly defaults: Readonly<Record<OperatorTemplate, FeatureDefault>>;
}

/** PREMIUM and ENTERPRISE. */
const OP_PREMIUM = { FREE: OFF, BASIC: OFF, PREMIUM: ON, ENTERPRISE: ON } as const;

/**
 * Corbin's plan values (2026-10-17): FREE has no portal sign-in; BASIC is sign-in and tickets
 * only, all history; PREMIUM is every feature with 3 API keys; ENTERPRISE is everything with 15.
 */
export const OPERATOR_FEATURE_CATALOG = [
  {
    id: 'OP_PORTAL_LOGIN',
    name: 'Portal sign-in',
    description: "The company's people can sign in to the operator portal.",
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 1000,
    defaults: { FREE: OFF, BASIC: ON, PREMIUM: ON, ENTERPRISE: ON },
  },
  {
    id: 'OP_TICKETS',
    name: 'Tickets',
    description:
      "Run tickets from the company's haulers. The limit is the days of history shown; none means all of it.",
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: true,
    sortOrder: 1010,
    defaults: {
      FREE: { enabled: true, limit: 30 },
      // Corbin: BASIC is "view tickets", all of them (Session 2's review of PR 6, MED1).
      BASIC: ON,
      PREMIUM: ON,
      ENTERPRISE: ON,
    },
  },
  {
    id: 'OP_VOLUMES',
    name: 'Volumes',
    description: 'Monthly volumes by site and hauler.',
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 1020,
    defaults: OP_PREMIUM,
  },
  {
    id: 'OP_TANK_LEVELS',
    name: 'Tank levels',
    description: "Tank levels from the haulers' readings.",
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 1030,
    defaults: OP_PREMIUM,
  },
  {
    id: 'OP_ROUTES_PUMPERS',
    name: 'Routes and pumpers',
    description: 'Routes, foremen, tank contacts, coverage and contact load emails.',
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 1040,
    defaults: OP_PREMIUM,
  },
  {
    id: 'OP_PUMPER_MOBILE',
    name: 'Pumpers on mobile',
    description: 'Pumper mode in the mobile app.',
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 1050,
    defaults: OP_PREMIUM,
  },
  {
    id: 'OP_HAUL_REQUESTS',
    name: 'Haul requests',
    description: 'Request hauls from the portal or the mobile app.',
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 1060,
    defaults: OP_PREMIUM,
  },
  {
    id: 'OP_API',
    name: 'Operator API',
    description:
      "API access to the company's hauler data. The limit is the number of active API keys.",
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: true,
    sortOrder: 1070,
    defaults: {
      FREE: OFF,
      BASIC: OFF,
      PREMIUM: { enabled: true, limit: 3 },
      ENTERPRISE: { enabled: true, limit: 15 },
    },
  },
  // 13.11 (D-26), migration 0263: not OP_API, which would share the key-count limit. The limit is
  // the number of gateways. The defaults are assumed (13-RESEARCH open question 3; Corbin's call).
  {
    id: 'OP_MQTT',
    name: 'MQTT tank readings',
    description:
      "Tank levels or volumes sent by the company's gateways over MQTT. The limit is the number of gateways.",
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: true,
    sortOrder: 1080,
    defaults: {
      FREE: OFF,
      BASIC: OFF,
      PREMIUM: { enabled: true, limit: 5 },
      ENTERPRISE: { enabled: true, limit: 25 },
    },
  },
  // Operator webhooks PR 1 (0239; DEC-242 follow-up): the company's load events POSTed to its
  // own endpoints. The limit is the number of endpoints. OP_REPORTS is 1100 (seeded by 0241).
  {
    id: 'OP_WEBHOOKS',
    name: 'Webhooks',
    description:
      "The company's load events sent to its own systems as they happen. The limit is the number of endpoints.",
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: true,
    sortOrder: 1090,
    defaults: {
      FREE: OFF,
      BASIC: OFF,
      PREMIUM: { enabled: true, limit: 3 },
      ENTERPRISE: { enabled: true, limit: 10 },
    },
  },
  {
    // Operator Reports (a), migration 0241 (Corbin 2026-10-19): CSV / Excel export of Tickets and
    // Volumes. The ticket PDF is not behind this: it rides on OP_TICKETS, on every plan.
    id: 'OP_REPORTS',
    name: 'Reports',
    description: 'Export tickets and volumes to CSV or Excel.',
    group: 'OPERATOR_PORTAL',
    isDataCapture: false,
    hasLimit: false,
    sortOrder: 1100,
    defaults: OP_PREMIUM,
  },
] as const satisfies readonly OperatorFeatureDefinition[];

export type OperatorFeatureId = (typeof OPERATOR_FEATURE_CATALOG)[number]['id'];

/** Every feature id, hauler and operator. */
export type FeatureId = HaulerFeatureId | OperatorFeatureId;

export const HAULER_FEATURE_IDS: readonly HaulerFeatureId[] = FEATURE_CATALOG.map((f) => f.id);
export const OPERATOR_FEATURE_IDS: readonly OperatorFeatureId[] = OPERATOR_FEATURE_CATALOG.map(
  (f) => f.id,
);
export const FEATURE_IDS: readonly FeatureId[] = [...HAULER_FEATURE_IDS, ...OPERATOR_FEATURE_IDS];

export function isFeatureId(value: string): value is FeatureId {
  return (FEATURE_IDS as readonly string[]).includes(value);
}

export function isHaulerFeatureId(value: string): value is HaulerFeatureId {
  return (HAULER_FEATURE_IDS as readonly string[]).includes(value);
}

export function isOperatorFeatureId(value: string): value is OperatorFeatureId {
  return (OPERATOR_FEATURE_IDS as readonly string[]).includes(value);
}

/** One feature's effective state for a hauler, as the apps receive it. */
export interface EffectiveFeature {
  readonly enabled: boolean;
  /** Present only for a feature with a limit, while it is enabled. */
  readonly limit?: number;
}

/**
 * What the profile and the mobile reference pull carry. Empty for a tenant that is not a
 * hauler. A feature missing from the map is OFF: an older app meeting a newer feature id
 * fails closed on the new feature only.
 */
export type EffectiveFeatureMap = Partial<Record<FeatureId, EffectiveFeature>>;

/**
 * §58 PR 4 — EXPRESS: the one customer a hauler on the EXPRESS template works for (migration
 * 0195). Sent next to `features` on the profile and the mobile tenant-settings feed; null when the
 * hauler is not on EXPRESS. The apps lock their customer pickers to it. No plan or tier name
 * travels with it (§58).
 */
export interface ExpressCustomer {
  /** platform_operators.id, the catalog operator. */
  readonly operatorId: string;
  readonly name: string;
  /** The hauler's active customer rows (hauler_operators.id) for that operator. */
  readonly haulerOperatorIds: ReadonlyArray<string>;
}

/** Read one feature from a map; missing means off. */
export function featureEnabled(map: EffectiveFeatureMap, id: FeatureId): boolean {
  return map[id]?.enabled === true;
}

// ── §58 PR 2: the platform admin's view of one hauler (GET /v1/platform/haulers/:id/plan) ─

/** Templates a platform admin can apply directly. EXPRESS also needs its customer (PR 4). */
export const DIRECT_TEMPLATES = ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'] as const;
export type DirectTemplate = (typeof DIRECT_TEMPLATES)[number];

export type FeatureSource = 'TEMPLATE' | 'OVERRIDE' | 'DATA_CAPTURE';

export interface HaulerFeatureOverrideView {
  readonly enabled: boolean;
  readonly limit: number | null;
  /** ISO; null = until cleared. */
  readonly expiresAt: string | null;
  /** True when the expiry has passed but the nightly cleanup has not removed it yet. */
  readonly expired: boolean;
  readonly reason: string;
  readonly updatedAt: string;
}

export interface HaulerFeatureView {
  readonly id: FeatureId;
  readonly name: string;
  readonly description: string;
  readonly group: FeatureGroup;
  readonly isDataCapture: boolean;
  readonly hasLimit: boolean;
  readonly templateDefault: FeatureDefault;
  readonly effective: {
    readonly enabled: boolean;
    readonly limit: number | null;
    readonly source: FeatureSource;
    readonly expiresAt: string | null;
  };
  readonly override: HaulerFeatureOverrideView | null;
}

export interface HaulerPlanHistoryEntry {
  readonly at: string;
  readonly action: string;
  readonly byName: string | null;
  readonly oldValues: Record<string, unknown> | null;
  readonly newValues: Record<string, unknown> | null;
  readonly reason: string | null;
}

export interface HaulerPlanView {
  readonly tenantId: string;
  readonly template: FeatureTemplate;
  /** False when the hauler has no plan row and reads as DEFAULT_FEATURE_TEMPLATE. */
  readonly hasPlanRow: boolean;
  readonly updatedAt: string | null;
  /**
   * The hauler's company timezone (hauler_tenant_settings.timezone, DEC-004). A trial ends at
   * the end of a day in THIS zone, not the admin's.
   */
  readonly timezone: string;
  /** EXPRESS's one customer (PR 4); null otherwise. */
  readonly expressOperator: { readonly id: string; readonly name: string } | null;
  readonly features: readonly HaulerFeatureView[];
  /** Newest first, at most 50. */
  readonly history: readonly HaulerPlanHistoryEntry[];
}

// ── OPP PR 6: the platform admin's view of one operator company's plan ───────────────────
// (GET /v1/platform/operator-plans/:tenantId)

export interface OperatorFeatureView {
  readonly id: OperatorFeatureId;
  readonly name: string;
  readonly description: string;
  readonly group: 'OPERATOR_PORTAL';
  readonly hasLimit: boolean;
  readonly templateDefault: FeatureDefault;
  readonly effective: {
    readonly enabled: boolean;
    /** null = no limit (unlimited for a feature that has one). */
    readonly limit: number | null;
    readonly source: FeatureSource;
    readonly expiresAt: string | null;
  };
  readonly override: HaulerFeatureOverrideView | null;
}

export interface OperatorPlanView {
  readonly tenantId: string;
  readonly template: OperatorTemplate;
  /** False when the company has no plan row and reads as DEFAULT_OPERATOR_TEMPLATE. */
  readonly hasPlanRow: boolean;
  readonly updatedAt: string | null;
  /** Active users allowed; null = no limit (Corbin: no template default). */
  readonly userSeatLimit: number | null;
  /** tenant_users with status ACTIVE (imported-not-invited included). */
  readonly activeUsers: number;
  /** The zone a trial's end date is read in (operators have no company timezone yet). */
  readonly timezone: string;
  readonly features: readonly OperatorFeatureView[];
  /** Newest first, at most 50. */
  readonly history: readonly HaulerPlanHistoryEntry[];
}
